/**
 * Тексты документов, которые пишет админка: политика конфиденциальности и
 * пользовательское соглашение (R-115). Сервер отдаёт страницу на языке
 * `X-Lang`, а если на нём текста нет — по-русски; какой язык пришёл, говорит
 * поле `lang`.
 */
import { useQuery } from '@tanstack/react-query'
import { isApiError, type components } from '@dorixona/api'
import { parseMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'

type TextPageDto = components['schemas']['TextPage']

export type TextPageSlug = 'privacy' | 'terms'

export interface TextPage {
  title: string
  /** HTML редактора админки (или простой текст) — на экран только через sanitize.ts. */
  body: string
  /** Язык, на котором пришёл текст: может отличаться от языка интерфейса. */
  lang: string
  /** Последняя правка, мс; 0 — сервер её не прислал. */
  updatedAt: number
}

export function toTextPage(dto: TextPageDto): TextPage {
  return {
    title: dto.title ?? '',
    body: dto.body ?? '',
    lang: dto.lang ?? 'ru',
    updatedAt: parseMs(dto.updated_at),
  }
}

/**
 * Страница на языке интерфейса. `null` — админка её ещё не написала (404):
 * экран тогда показывает встроенный текст, а не ошибку. Язык входит в ключ,
 * поэтому смена RU/UZ запрашивает страницу заново. Заголовок `X-Lang` ставим
 * сами из того же языка, что в ключе: кэш не смешает переводы, даже если
 * язык клиента API отстанет на кадр.
 */
export function useTextPage(slug: TextPageSlug, lang: string) {
  const api = useApi()
  return useQuery({
    queryKey: keys.page(slug, lang),
    queryFn: async (): Promise<TextPage | null> => {
      try {
        const dto = await api.get<TextPageDto>(`/pages/${slug}`, {
          auth: false,
          headers: { 'X-Lang': lang },
        })
        return toTextPage(dto)
      } catch (error) {
        if (isApiError(error) && error.code === 'not_found') return null
        throw error
      }
    },
  })
}
